import { type CompanyTag, type Currency, type FundingRound, type Headcount } from "@prisma/client";
import { type AppContext } from "~/lib/context";
import { chain, isNumber } from "~/lib/lodash";
import { arrayHasValues } from "~/lib/utils";
import { Measure } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { type WeightedJob } from "~/services/job";
import { computeMarketCompensationData } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketCompensationData";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { type EmployeeLocationsForPredictiveBenchmark } from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { type PercentileBounds } from "~/services/percentiles";

type ComputeMarketCompensationStatsOptions = {
  currency: Currency;
  weightedJobs: WeightedJob[];
  level?: EmployeeLevel;
  locations: EmployeeLocationsForPredictiveBenchmark;
  percentiles: PercentileBounds;
  includeEmployees?: boolean;
  includeGenderData?: boolean;
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
  industries?: CompanyTag[];
  fundingRounds?: FundingRound[];
};

export const computeMarketVariableCompensationStats = async (
  ctx: AppContext,
  datasetEmployees: MarketDataDatasetEmployee[],
  options: ComputeMarketCompensationStatsOptions
) => {
  const companiesStats = chain(datasetEmployees)
    .groupBy((employee) => {
      return employee.companyId ?? employee.churnedCompanyId;
    })
    .values()
    .map((employees) => {
      const employeesWithVariableCompensation = employees.filter((employee) => {
        return isNumber(employee.onTargetBonus) && employee.onTargetBonus > 0;
      });
      const hasMajorityOfEmployeesWithVariableCompensation =
        employees.length <= employeesWithVariableCompensation.length * 2;

      return {
        employees,
        employeesWithVariableCompensation,
        hasMajorityOfEmployeesWithVariableCompensation,
      };
    })
    .value();

  const relevantCompanies = companiesStats.filter((stats) => {
    return stats.hasMajorityOfEmployeesWithVariableCompensation;
  });

  const relevantEmployees = relevantCompanies.flatMap((company) => {
    return company.employeesWithVariableCompensation;
  });

  const distinctCompaniesCount = relevantCompanies.length;
  const companiesPercentage = arrayHasValues(companiesStats) ? distinctCompaniesCount / companiesStats.length : 0;

  const variableCompensationData = await computeMarketCompensationData(ctx, relevantEmployees, {
    ...options,
    measures: [Measure.BaseSalary, Measure.OnTargetBonus],
  });

  const medianBaseSalary = variableCompensationData.data?.measures.baseSalary.median ?? null;
  const medianAmount = variableCompensationData.data?.measures.onTargetBonus.median ?? null;

  const baseSalaryProportion =
    isNumber(medianAmount) && isNumber(medianBaseSalary) ? medianAmount / medianBaseSalary : null;

  return {
    distinctCompaniesCount,
    medianAmount,
    baseSalaryProportion,
    companiesPercentage,
  };
};
